import startCase from "lodash/startCase";

import { getModifiedMenuList } from "@components/store/multi-level-menu/serializer";

import { formatPrice, convertPrice } from "@helpers/currency";
import { createMenuCodeMapping, flattenMenuChildrenTree, serializeMenu } from "@helpers/menu";
import { CategoryVariant, CE_05, DynamicSlicesData } from "@helpers/plasmic/slices";

import { ALL_SUBCATEGORY_CODE } from "@constants/routes";

const RSC_INTERNAL_FIELDS = [
  "parentId",
  "lft",
  "allChildTaxonIds",
  "themeCollection",
  "isDisabled",
  "isDummyStoreMerch",
  "forSwag",
] as const;

type MenuNode = Record<string, unknown> & { children?: MenuNode[] };

const stripInternalFields = <T extends MenuNode>(items: T[]): T[] => {
  for (const item of items) {
    for (const field of RSC_INTERNAL_FIELDS) {
      Reflect.deleteProperty(item, field);
    }
    if (item.children?.length) {
      stripInternalFields(item.children);
    }
  }
  return items;
};

const convertPriceInGivenCurrency = (
  price,
  baseCurrency,
  toCurrency,
  conversionRates,
  options = {},
) => {
  if (!price) return price;

  if (baseCurrency === toCurrency || !baseCurrency) {
    return options.ceilRequired || options.ceilRequiredBeforeConversion
      ? Math.ceil(price.toFixed(4))
      : price;
  } else {
    return convertPrice(
      {
        from: baseCurrency,
        to: toCurrency,
        value: price,
        conversionRates,
      },
      options,
    );
  }
};

export const formatPriceInGivenCurrency = ({
  price,
  baseCurrency,
  toCurrency,
  storeCurrencyPrefix,
  storeCurrencySuffix,
  conversionRates,
  options = {},
}) => {
  if (!options.formatZero && !price) return price;
  let convertedPrice = convertPriceInGivenCurrency(
    price,
    baseCurrency,
    toCurrency,
    conversionRates,
    options,
  );
  return formatPrice(convertedPrice, {
    prefix: storeCurrencyPrefix,
    suffix: storeCurrencySuffix,
    currencyCode: toCurrency,
    ...(options.currencyDisplay && { currencyDisplay: options.currencyDisplay }),
  });
};

export const getCategoriesData = (parentCategories: any[], skipTitleHumanization: boolean) => {
  const categories =
    flattenMenuChildrenTree(parentCategories).filter(
      (item: any) => item.code !== ALL_SUBCATEGORY_CODE,
    ) || [];

  const allCategories = categories.map((item: any) => ({
    ...item,
    name: skipTitleHumanization ? item.name : startCase(item.name),
  }));
  return {
    parentCategories,
    allCategories,
  };
};

export const getDynamicSlicesData = ({
  isMenuAndStoreDataLoading,
  store,
  menuData,
  countryIso,
}: {
  isMenuAndStoreDataLoading?: boolean;
  store?: any;
  menuData?: any;
  countryIso: string;
}): DynamicSlicesData => {
  if (isMenuAndStoreDataLoading) {
    return {
      isMenuAndStoreDataLoading,
      parentCategories: [],
      allCategories: [],
      countryIso,
    };
  }

  const menu = serializeMenu({
    data: menuData,
    countryIso,
    store,
  });

  const totalLevel = menu?.totalLevel;
  const isMultiLevelMenu = menu?.totalLevel > 2;

  const finalMenuList = getModifiedMenuList({
    list: menu?.children,
    parentItemCode: "",
    level: 1,
    isPreviewCatalogMode: false,
    isMultiLevelMenu,
    totalLevel,
    forDynamicSlices: true,
  });

  const { parentCategories } = getCategoriesData(finalMenuList, menu?.skipTitleHumanization);

  const slimParentCategories = stripInternalFields(parentCategories);

  const slimAllCategories = (flattenMenuChildrenTree(slimParentCategories) as MenuNode[])
    .filter((item) => item.code !== ALL_SUBCATEGORY_CODE)
    .map((item) => ({
      ...item,
      name: menu?.skipTitleHumanization ? item.name : startCase(item.name as string),
    }));

  const projectedMenu = menu
    ? {
        ...menu,
        children: slimParentCategories,
        allChildren: ((menu.allChildren || []) as MenuNode[]).map(
          ({ linkedTaxonResources, isDisabled, isDummyStoreMerch, code }) => ({
            linkedTaxonResources,
            isDisabled,
            isDummyStoreMerch,
            code,
          }),
        ),
      }
    : menu;

  const menuMapping = createMenuCodeMapping(projectedMenu);

  return {
    store,
    menu: projectedMenu,
    parentCategories: slimParentCategories,
    allCategories: slimAllCategories,
    menuMapping,
    countryIso,
  };
};

export const getCategoriesByCategorySlug = ({
  variant,
  categorySlug,
  allCategoriesData,
  skipTitleHumanization,
}: {
  variant: CategoryVariant;
  categorySlug: string;
  allCategoriesData: any[];
  skipTitleHumanization: boolean;
}) => {
  if (!Array.isArray(allCategoriesData) || allCategoriesData.length === 0) {
    return { parentCategories: [], allCategories: [] };
  }

  const category = allCategoriesData.find((category) => category.code === categorySlug);

  if (!category) return { parentCategories: [], allCategories: [] };

  const children = category?.children || [];
  const categories = variant === CE_05 ? [category, ...children] : children;

  return getCategoriesData(categories, skipTitleHumanization);
};
